import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Eye, 
  Target, 
  Percent, 
  DollarSign,
  Filter
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';

// API et Types
import { 
  getNoConsentDashboardData, 
  getNoConsentFilterOptions,
  NoConsentFilters,
  NoConsentDashboardData,
  NoConsentFilterOptions
} from '../../../api/noConsentApi';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';

// No Consent Components
import { NoConsentFiltersComponent } from './NoConsentFilters';
import { NoConsentTable } from './NoConsentTable';
import { NoConsentNoService } from './NoConsentNoService';

// Chart components
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const NoConsentDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  // États
  const [dashboardData, setDashboardData] = useState<NoConsentDashboardData | null>(null);
  const [filterOptions, setFilterOptions] = useState<NoConsentFilterOptions | null>(null);
  const [filters, setFilters] = useState<NoConsentFilters>({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoService, setHasNoService] = useState(false);

  // Récupérer les données du dashboard
  const fetchDashboardData = async (currentFilters?: NoConsentFilters) => {
    try {
      console.log('🎯🎯🎯 NOCONSENT DASHBOARD: fetchDashboardData START');
      console.log('🎯 currentUser:', currentUser);
      
      // Récupérer l'ID d'impersonation depuis localStorage
      const impersonatedUserId = localStorage.getItem('impersonatedUserId');
      console.log('🎯 impersonatedUserId from localStorage:', impersonatedUserId);
      console.log('🎯 currentFilters:', currentFilters);
      console.log('🎯 filters:', filters);
      
      setIsLoading(true);
      setError(null);
      
      // Utiliser les filtres tels quels, sans modification automatique
      const finalFilters = { ...(currentFilters || filters) };
      
      console.log('🎯 finalFilters to send:', finalFilters);
      
      const data = await getNoConsentDashboardData(finalFilters);
      console.log('🎯 API response data:', data);
      
      setDashboardData((data as any).data);
      console.log('🎯 Dashboard data set successfully');
    } catch (error: any) {
      console.error('🎯 ERROR in fetchDashboardData:', error);
      console.error('Error fetching No Consent dashboard data:', error);
      setError(t('noconsent.errorFetchingData'));
    } finally {
      setIsLoading(false);
    }
  };

  // Récupérer les options de filtres
  const fetchFilterOptions = async () => {
    try {
      const options = await getNoConsentFilterOptions();
      setFilterOptions((options as any).data);
      
      // Si aucun site n'est disponible pour un utilisateur non-admin, 
      // cela signifie qu'aucun service NoConsentOutbrain n'est configuré
      if ((options as any).data.sites.length === 0 && currentUser?.role !== 'ADMIN') {
        setHasNoService(true);
      } else {
        setHasNoService(false);
      }
    } catch (error: any) {
      console.error('Error fetching No Consent filter options:', error);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    fetchFilterOptions();
    fetchDashboardData();
  }, []);

  // Gestionnaire de changement de filtre
  const handleFiltersChange = (newFilters: NoConsentFilters) => {
    setFilters(newFilters);
    fetchDashboardData(newFilters);
  };

  // Formater les valeurs
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Fonction pour créer les cartes de graphiques avec métriques
  const renderChart = (title: string, children: React.ReactNode, metric?: { value: number; type: 'sum' | 'average' | 'currency' | 'percentage' }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold flex justify-between items-center">
          <span>{title}</span>
          {metric && (
            <span className="text-sm font-bold text-foreground">
              {metric.type === 'currency' ? formatCurrency(metric.value) : 
               metric.type === 'percentage' ? formatPercentage(metric.value) :
               formatNumber(metric.value)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          {children}
        </div>
      </CardContent>
    </Card>
  );

  // Configuration des graphiques
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 3
        }
      },
      y: {
        beginAtZero: true
      }
    }
  };

  // Créer les données de graphique
  // Créer les données de graphique à partir des métriques quotidiennes
  const createChartData = (metrics: any[], dataKey: string, label: string, color: string) => {
    return {
      labels: metrics.map((item) => new Date(item.date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      })),
      datasets: [
        {
          label,
          data: metrics.map((item) => item[dataKey] || 0),
          borderColor: color,
          backgroundColor: color + '20',
          fill: true,
          tension: 0.1,
        },
      ],
    };
  };  if (!currentUser) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('auth.notAuthenticated')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-7rem)] overflow-auto">
      <div className="p-6 space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">{t('noconsent.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('noconsent.subtitle')}
            </p>
          </div>
        </div>

        {/* Affichage du composant "No Service" si aucun service n'est configuré */}
        {!isLoading && hasNoService && (
          <NoConsentNoService />
        )}

        {/* Contenu normal du dashboard (filtres, métriques, etc.) */}
        {!isLoading && !hasNoService && (
          <>
        {/* Filtres */}
        <div className="animate-in fade-in duration-500 delay-300">
          {isLoading ? (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {t('common.filters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {t('common.filters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <NoConsentFiltersComponent
                  filters={filters}
                  filterOptions={filterOptions}
                  onChange={handleFiltersChange}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Messages d'erreur */}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Métriques de résumé */}
        {dashboardData && (
          <Card className="animate-in fade-in duration-500 delay-500">
            <CardHeader>
              <CardTitle>{t('noconsent.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-none shadow-sm bg-gradient-to-r from-blue-500 to-blue-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('noconsent.revenue')}
                        </h3>
                        <DollarSign className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatCurrency(dashboardData.summary.totalRevenue)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-green-500 to-green-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('noconsent.views')}
                        </h3>
                        <Eye className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatNumber(dashboardData.summary.totalViews)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-orange-500 to-orange-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('noconsent.clicks')}
                        </h3>
                        <Target className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatNumber(dashboardData.summary.totalClicks)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-purple-500 to-purple-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          {t('noconsent.viewability')}
                        </h3>
                        <Percent className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatPercentage(dashboardData.summary.averageViewability)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Graphiques - 4 charts comme demandé */}
        {dashboardData && (
          <div className="animate-in fade-in duration-500 delay-700">
            {/* Première ligne : Revenue et Paid Page Views */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Revenus */}
              {renderChart(
                t('noconsent.revenue'),
                <Line 
                  data={createChartData(dashboardData.dailyMetrics, 'revenue', t('noconsent.revenue'), '#3b82f6')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.summary.totalRevenue, 
                  type: 'currency' 
                }
              )}

              {/* Paid Page Views */}
              {renderChart(
                t('noconsent.paidPageViews'),
                <Line 
                  data={createChartData(dashboardData.dailyMetrics, 'paidPvs', t('noconsent.paidPageViews'), '#10b981')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.dailyMetrics.reduce((sum, item) => sum + item.paidPvs, 0), 
                  type: 'sum' 
                }
              )}
            </div>

            {/* Deuxième ligne : Paid Clicks et Viewability */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Paid Clicks */}
              {renderChart(
                t('noconsent.paidClicks'),
                <Line 
                  data={createChartData(dashboardData.dailyMetrics, 'paidClicks', t('noconsent.paidClicks'), '#f59e0b')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.dailyMetrics.reduce((sum, item) => sum + item.paidClicks, 0), 
                  type: 'sum' 
                }
              )}

              {/* Viewability */}
              {renderChart(
                t('noconsent.viewability'),
                <Line 
                  data={createChartData(dashboardData.dailyMetrics, 'viewabilityRate', t('noconsent.viewability'), '#ef4444')}
                  options={chartOptions}
                />,
                { 
                  value: dashboardData.summary.averageViewability, 
                  type: 'percentage' 
                }
              )}
            </div>
          </div>
        )}

        {/* Tableau de données quotidiennes */}
        {dashboardData && (
          <div className="animate-in fade-in duration-500 delay-900">
            <NoConsentTable
              dailyMetrics={dashboardData.dailyMetrics}
              isLoading={isLoading}
            />
          </div>
        )}

        {/* État de chargement */}
        {isLoading && !dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array(4).fill(0).map((_, i) => (
              <Card key={i}>
                <CardHeader className="space-y-0 pb-2">
                  <div className="h-4 bg-muted rounded animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};